// @todo check hardcoded HR_ADD_ON_ID used across this file, should be fetched from the tenant-subscription store instead
import { HR_EVENT_SOURCE } from '@webapp-next/modules/people/analytics';
import { EVENT_SOURCE as HIRE_EVENT_SOURCE } from '@apps-hire/analytics';
import DeputyHrImage from '@/apps/billing-shared/src/assets/deputy-hr.svg';
import DeputyHRAddOnDetailsImage from '@/apps/billing-shared/src/assets/add-on-details.png';
import AdvancedReportingImage from '@/apps/billing-shared/src/assets/advanced-reporting.svg';
import AdvancedReportingAddOnDetailsImage from '@/apps/billing-shared/src/assets/reporting-banner.png';
import AdvancedReportingModalImage from '@/apps/billing-shared/src/components/advanced-reporting-modal.png';
import MessagingAddOnModalImage from '@/modules/chat/assets/messaging-add-on-banner.png';
import CardHrAddOn from '@/apps/billing-shared/src/assets/card-hr-add-on.svg';
import CardAdvancedReportingAddOn from '@/apps/billing-shared/src/assets/card-advanced-reporting-add-on.svg';
import checkFF from '@/lib/feature-flag';
import auLocalization from './locales/en-au.json';
import gbLocalization from './locales/en-gb.json';
import usLocalization from './locales/en-us.json';

// Payment frequency constants
export const PAYMENT_FREQUENCY = {
  MONTHLY: 'monthly',
  ANNUAL_UPFRONT: 'annual',
  ANNUAL_PAID_MONTHLY: 'annualPaidMonthly',
};

export const SUBSCRIPTION_CHANNEL = {
  DIRECT: 'DIRECT',
  CPQ: 'CPQ',
};

// FE Add-ons
export const addOns = {
  HR_ADD_ON_ID: 'hr-suite-standard',
  PAYROLL_ADD_ON_ID: 'payroll-standard',
  ADVANCED_REPORTING_ADD_ON_ID: 'reporting-standard',
  MESSAGING_ADD_ON_ID: 'messaging-standard',
};

export const annualAddOns = {
  HR_ANNUAL_ADD_ON_ID: 'hr-suite-annual',
  PAYROLL_ANNUAL_ADD_ON_ID: 'payroll-annual',
  HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID: 'hr-suite-annual-paid-monthly',
  PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID: 'payroll-annual-paid-monthly',
  ADVANCED_REPORTING_ANNUAL_ADD_ON_ID: 'reporting-annual',
  ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY_ADD_ON_ID:
    'reporting-annual-paid-monthly',
  MESSAGING_ANNUAL_ADD_ON_ID: 'messaging-annual',
  MESSAGING_ANNUAL_PAID_MONTHLY_ADD_ON_ID: 'messaging-annual-paid-monthly',
};

export const allAddOns = {
  ...addOns,
  ...annualAddOns,
};

// The add-ons that make up the AIO bundle (standard + annual variants). Kept
// as HR and Payroll families so callers that need "cart has an HR AND a
// Payroll" (the checkout banner) can check each family, while callers that
// only need "is this add-on part of the bundle" (the add-on pill) use the
// combined list. Single source of truth so the two gates can't drift.
// Promo codes for these products must not auto-apply or show a banner in an
// AIO-bundle checkout; other add-ons (Analytics, Messaging) are unaffected.
export const AIO_HR_ADD_ON_IDS: string[] = [
  addOns.HR_ADD_ON_ID,
  annualAddOns.HR_ANNUAL_ADD_ON_ID,
  annualAddOns.HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
];
export const AIO_PAYROLL_ADD_ON_IDS: string[] = [
  addOns.PAYROLL_ADD_ON_ID,
  annualAddOns.PAYROLL_ANNUAL_ADD_ON_ID,
  annualAddOns.PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
];
export const AIO_BUNDLE_ADD_ON_IDS: string[] = [
  ...AIO_HR_ADD_ON_IDS,
  ...AIO_PAYROLL_ADD_ON_IDS,
];

// Hash query marking an aio_bundle entry made from a choose-plan modal (tenant
// or Once), so cancelling the bundle checkout returns to that modal instead of
// the subscription page. Travels in the URL as the tenant entry is cross-origin.
export const AIO_BUNDLE_SOURCE_PARAM = 'source';
export const AIO_BUNDLE_SOURCE_CHOOSE_PLAN = 'choose-plan';
export const AIO_BUNDLE_CHOOSE_PLAN_QUERY = `${AIO_BUNDLE_SOURCE_PARAM}=${AIO_BUNDLE_SOURCE_CHOOSE_PLAN}`;

// Zuora holds the bundle discount as rate plan `aio-2026`, which carries no
// pricing data — the rate is stated here for display only. The amount actually
// billed comes from the rate plan itself.
export const AIO_BUNDLE_DISCOUNT_IDENTIFIER = 'aio-2026';
export const AIO_BUNDLE_DISCOUNT_RATE = 0.15;

// Display-only mirrors of the Zuora AIO promo rate plans (GROWTH-3580). The backend
// applies the actual discount (DPTY25AIO / DPTY3AIO); these drive the checkout summary
// preview only, and MUST be updated by hand if the Zuora offer changes.
// 25% = DPTY25AIO (annual upfront) and DPTY3AIO (APM: 3 of 12 instalments free);
// both resolve to a quarter off the post-bundle price.
export const AIO_ANNUAL_UPFRONT_PROMO_RATE = 0.25;

export const zuoraBasePlanChargeMap = {
  PREMIUM_YEARLY: 'Premium Annual Upfront Charges',
  LITE_ANNUAL: 'Lite Annual Upfront Charges',
  CORE_ANNUAL: 'Core Annual Upfront Charges',
  PRO_ANNUAL: 'Pro Annual Upfront Charges',
  GOOD_ANNUAL_PAID_MONTHLY: 'Lite Monthly Upfront Charges',
  BETTER_ANNUAL_PAID_MONTHLY: 'Core Monthly Upfront Charges',
  BEST_ANNUAL_PAID_MONTHLY: 'Pro Monthly Upfront Charges',
};

export const zuoraDiscountChargeMap = {
  GENERAL_DISCOUNT: 'Promotional Discount',
};

export const zuoraAddOnChargeMap = {
  HR_ANNUAL: [
    'HR Annual Upfront Charges',
    'HR Suite Onboarding Charges',
    'HR Annual Pricing Monthly Overage Pricing',
    'HR Monthly Upfront Charges',
  ],
  PAYROLL_ANNUAL: [
    'Payroll Annual Upfront Charges',
    'Payroll Annual Pricing Monthly Overage Pricing',
    'Payroll Monthly Upfront Charges',
  ],
  ADVANCED_REPORTING_ANNUAL: [
    'Analytics+ Annual Upfront Charges',
    'Analytics+ Annual Pricing Monthly Overage Pricing',
    'Analytics+ Monthly Upfront Charges',
  ],
  MESSAGING_ANNUAL: [
    'Messaging+ Annual Upfront Charges',
    'Messaging+ Annual Pricing Monthly Overage Pricing',
    'Messaging+ Monthly Upfront Charges',
  ],
};

export const SUBSCRIPTION = {
  FREE: 0,
  PAID1: 1,
  PRO_TRIAL: 2,
  PREMIUM: 1,
  FLEXI: 3,
  MAXI: 4,
  SCHEDULING_ONLY: 5,
  TIMESHEET_ONLY: 6,
  ENTERPRISE: 10,
  PREMIUM_YEARLY: 11,
  MOBILE_ONLY: 20,
  FREEMIUM: 30,
  GOOD: 40,
  GOOD_ANNUAL: 41,
  GOOD_ANNUAL_PAID_MONTHLY: 43,
  BETTER: 50,
  BETTER_ANNUAL: 51,
  BETTER_ANNUAL_PAID_MONTHLY: 53,
  BEST: 60,
  BEST_ANNUAL: 61,
  BEST_ANNUAL_PAID_MONTHLY: 63,
};

export type SubscriptionPlan = number;

export const SUBSCRIPTION_RELATED_PLANS = {
  [SUBSCRIPTION.PREMIUM]: [SUBSCRIPTION.PREMIUM_YEARLY],
  [SUBSCRIPTION.PREMIUM_YEARLY]: [SUBSCRIPTION.PREMIUM],
  [SUBSCRIPTION.GOOD]: [
    SUBSCRIPTION.GOOD_ANNUAL,
    SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
  ],
  [SUBSCRIPTION.GOOD_ANNUAL]: [
    SUBSCRIPTION.GOOD,
    SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
  ],
  [SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY]: [
    SUBSCRIPTION.GOOD,
    SUBSCRIPTION.GOOD_ANNUAL,
  ],
  [SUBSCRIPTION.BETTER]: [
    SUBSCRIPTION.BETTER_ANNUAL,
    SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
  ],
  [SUBSCRIPTION.BETTER_ANNUAL]: [
    SUBSCRIPTION.BETTER,
    SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
  ],
  [SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY]: [
    SUBSCRIPTION.BETTER,
    SUBSCRIPTION.BETTER_ANNUAL,
  ],
  [SUBSCRIPTION.BEST]: [
    SUBSCRIPTION.BEST_ANNUAL,
    SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY,
  ],
  [SUBSCRIPTION.BEST_ANNUAL]: [
    SUBSCRIPTION.BEST,
    SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY,
  ],
  [SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY]: [
    SUBSCRIPTION.BEST,
    SUBSCRIPTION.BEST_ANNUAL,
  ],
};

type AddOnIdKeys = keyof typeof addOns;
export type AddOnId = (typeof addOns)[AddOnIdKeys];

type AnnualAddOnIdKeys = keyof typeof annualAddOns;
export type AnnualAddOnId = (typeof annualAddOns)[AnnualAddOnIdKeys];

type AllAddOnIdKeys = keyof typeof allAddOns;
export type AllAddOnId = (typeof allAddOns)[AllAddOnIdKeys];

export const ADD_ON_ORDERING: AllAddOnId[] = [
  allAddOns.PAYROLL_ADD_ON_ID,
  allAddOns.PAYROLL_ANNUAL_ADD_ON_ID,
  allAddOns.PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
  allAddOns.HR_ADD_ON_ID,
  allAddOns.HR_ANNUAL_ADD_ON_ID,
  allAddOns.HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
  allAddOns.MESSAGING_ADD_ON_ID,
  allAddOns.MESSAGING_ANNUAL_ADD_ON_ID,
  allAddOns.MESSAGING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
  allAddOns.ADVANCED_REPORTING_ADD_ON_ID,
  allAddOns.ADVANCED_REPORTING_ANNUAL_ADD_ON_ID,
  allAddOns.ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
];

export const countryCodes = {
  AU: 'AU',
  NZ: 'NZ',
  GB: 'GB',
  US: 'US',
  WW: 'WW',
};

export type CountryCode = keyof typeof countryCodes;

// The AIO bundle is offered in these countries only. UK (GB) and US are
// excluded: UK Payroll hasn't launched and US is out of scope for this release.
export const AIO_ELIGIBLE_COUNTRIES: string[] = [countryCodes.AU];

export type AddOnStore = {
  addOnId: AddOnId;
};

export type TrialLength = {
  unit: string;
  unitSingular: string;
  count: number;
};

export enum FeatureKeys {
  DOCUMENT_MANAGEMENT = 'documentManagement',
  DEPUTY_HIRE = 'deputyHire',
  ONBOARD_HIRE = 'onboardHire',
  TEAM_ENGAGEMENT = 'teamEngagement',
  LEAVE_MANAGEMENT = 'leaveManagement',
  CUSTOMIZE_REPORTS = 'customizeReports',
  KUDOS_REPORTING = 'kudosReporting',
  DRAG_DROP = 'dragDrop',
  SHIFT_INDICATORS = 'shiftIndicators',
  ADVANCED_MESSAGING = 'advancedMessaging',
  MESSAGE_HISTORY = 'messageHistory',
  PERMISSIONS = 'permissions',
  GROUP_CHAT_MEMBERS = 'groupChatMembers',
}

export type Features = {
  [key in keyof typeof countryCodes]?: {
    number?: FeatureKeys[];
  };
};

type Translations = {
  title: string;
  description: string;
  features: Partial<Record<FeatureKeys, string>>;
};

type AddOnEntry = {
  name: string;
  nameShort: string;
  imageSource: string;
  eventDetailsName: string;
  parentAddOn?: string;
  equivalentPaymentFrequencyPlans?: Record<string, string>;
  features: Features;
  availableGlobally: boolean;
  trialLength: TrialLength;
  confirmSubscriptionImageSource: string;
  translations: Translations;
  featureFlag: string;
  featureFlagPreSubscribe?: string;
  videoUrl: Record<string, string>;
  fallBackModalImageSource: string;
  visibilityState?: 'express_interest' | 'eligible' | 'hidden' | '' | null;
  expressInterestUrl?: string | null;
};

type AddOnDataMap = {
  [key: AddOnId]: AddOnEntry;
};

const commonFeatures = {
  [addOns.HR_ADD_ON_ID]: [
    FeatureKeys.DEPUTY_HIRE,
    ...(checkFF('hire-1687-hr-shift-pulse')
      ? [FeatureKeys.TEAM_ENGAGEMENT]
      : []),
    FeatureKeys.ONBOARD_HIRE,
    FeatureKeys.LEAVE_MANAGEMENT,
    FeatureKeys.DOCUMENT_MANAGEMENT,
    FeatureKeys.KUDOS_REPORTING,
  ],
  [addOns.ADVANCED_REPORTING_ADD_ON_ID]: [
    FeatureKeys.CUSTOMIZE_REPORTS,
    FeatureKeys.DRAG_DROP,
  ],
  [addOns.MESSAGING_ADD_ON_ID]: [
    FeatureKeys.SHIFT_INDICATORS,
    FeatureKeys.MESSAGE_HISTORY,
    FeatureKeys.PERMISSIONS,
    FeatureKeys.ADVANCED_MESSAGING,
    FeatureKeys.GROUP_CHAT_MEMBERS,
  ],
};

const usHrFeatures = [...commonFeatures[addOns.HR_ADD_ON_ID]];

// AU leave access is not gated by the HR add-on, so AU must not market it.
// GB and US genuinely bundle leave with the add-on and keep the shared list.
// The upgrade modal card is gated on LEAVE_MANAGEMENT, so AU copy overrides
// (getRegionalHrLeaveCardCopy) render only after the key returns to this list.
const hrFeaturesWithoutLeave = commonFeatures[addOns.HR_ADD_ON_ID].filter(
  feature => feature !== FeatureKeys.LEAVE_MANAGEMENT
);

// TODO: This logic should exist in a "features" store
function getFeatureInAllPlans(features: FeatureKeys[]): {
  [key: number]: FeatureKeys[];
} {
  return Object.values(SUBSCRIPTION).reduce(
    (acc, subscription) => {
      acc[subscription] = features;
      return acc;
    },
    {} as { [key: number]: FeatureKeys[] }
  );
}

type allAddOnEntry = {
  name: string;
  nameShort: string;
  eventDetailsName: string;
  parentName?: string;
  parentNameShort?: string;
  parentAddOn?: string;
  paymentFrequency?: string;
  equivalentPaymentFrequencyPlans?: Record<string, string>;
  featureFlagAnnualAddOn?: string;
};

// General details of all versions of add-ons offered, including annual plans.
export const allAddOnDetails: Record<AddOnId | AnnualAddOnId, allAddOnEntry> = {
  [addOns.HR_ADD_ON_ID]: {
    name: 'Deputy HR',
    nameShort: 'HR',
    eventDetailsName: 'DEPUTY_HR',
    parentName: 'Deputy HR',
    parentNameShort: 'HR',
    parentAddOn: 'hr',
    paymentFrequency: PAYMENT_FREQUENCY.MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]: annualAddOns.HR_ANNUAL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: '',
  },
  [annualAddOns.HR_ANNUAL_ADD_ON_ID]: {
    name: 'Deputy HR Annual',
    nameShort: 'HR Annual',
    eventDetailsName: 'DEPUTY_HR_ANNUAL',
    parentName: 'Deputy HR',
    parentNameShort: 'HR',
    parentAddOn: 'hr',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_UPFRONT,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.HR_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: '',
  },
  [annualAddOns.HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID]: {
    name: 'Deputy HR Annual Paid Monthly',
    nameShort: 'HR Annual',
    eventDetailsName: 'DEPUTY_HR_ANNUAL_PAID_MONTHLY',
    parentName: 'Deputy HR',
    parentNameShort: 'HR',
    parentAddOn: 'hr',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.HR_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]: annualAddOns.HR_ANNUAL_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: '',
  },
  [addOns.ADVANCED_REPORTING_ADD_ON_ID]: {
    name: 'Analytics+',
    nameShort: 'Analytics+',
    parentName: 'Analytics+',
    parentNameShort: 'Analytics+',
    eventDetailsName: 'ADVANCED_REPORTING',
    parentAddOn: 'reporting',
    paymentFrequency: PAYMENT_FREQUENCY.MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]:
        annualAddOns.ADVANCED_REPORTING_ANNUAL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: 'wo-2641-annual-add-ons-analytics',
  },
  [annualAddOns.ADVANCED_REPORTING_ANNUAL_ADD_ON_ID]: {
    name: 'Analytics+ Annual',
    nameShort: 'Analytics+',
    parentName: 'Analytics+',
    parentNameShort: 'Analytics+',
    eventDetailsName: 'ADVANCED_REPORTING_ANNUAL',
    parentAddOn: 'reporting',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_UPFRONT,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.ADVANCED_REPORTING_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: 'wo-2641-annual-add-ons-analytics',
  },
  [annualAddOns.ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY_ADD_ON_ID]: {
    name: 'Analytics+ Annual Paid Monthly',
    nameShort: 'Analytics+ Annual',
    parentName: 'Analytics+',
    parentNameShort: 'Analytics+',
    eventDetailsName: 'ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY',
    parentAddOn: 'reporting',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.ADVANCED_REPORTING_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]:
        annualAddOns.ADVANCED_REPORTING_ANNUAL_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: 'wo-2641-annual-add-ons-analytics',
  },
  [addOns.PAYROLL_ADD_ON_ID]: {
    name: 'Deputy Payroll',
    nameShort: 'Payroll',
    eventDetailsName: 'DEPUTY_PAYROLL',
    parentName: 'Deputy Payroll',
    parentNameShort: 'Payroll',
    parentAddOn: 'payroll',
    paymentFrequency: PAYMENT_FREQUENCY.MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]: annualAddOns.PAYROLL_ANNUAL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: '',
  },
  [addOns.MESSAGING_ADD_ON_ID]: {
    name: 'Messaging+',
    nameShort: 'Messaging+',
    parentName: 'Messaging+',
    parentNameShort: 'Messaging+',
    eventDetailsName: 'MESSAGING_PLUS',
    parentAddOn: 'messaging',
    paymentFrequency: PAYMENT_FREQUENCY.MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]:
        annualAddOns.MESSAGING_ANNUAL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.MESSAGING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: 'wo-2642-annual-add-ons-messaging',
  },
  [annualAddOns.MESSAGING_ANNUAL_ADD_ON_ID]: {
    name: 'Messaging+ Annual',
    nameShort: 'Messaging+',
    parentName: 'Messaging+',
    parentNameShort: 'Messaging+',
    eventDetailsName: 'MESSAGING_PLUS_ANNUAL',
    parentAddOn: 'messaging',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_UPFRONT,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.MESSAGING_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.MESSAGING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: 'wo-2642-annual-add-ons-messaging',
  },
  [annualAddOns.MESSAGING_ANNUAL_PAID_MONTHLY_ADD_ON_ID]: {
    name: 'Messaging+ Annual Paid Monthly',
    nameShort: 'Messaging+',
    parentName: 'Messaging+',
    parentNameShort: 'Messaging+',
    eventDetailsName: 'MESSAGING_PLUS_ANNUAL_PAID_MONTHLY',
    parentAddOn: 'messaging',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.MESSAGING_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]:
        annualAddOns.MESSAGING_ANNUAL_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: 'wo-2642-annual-add-ons-messaging',
  },
  [annualAddOns.PAYROLL_ANNUAL_ADD_ON_ID]: {
    name: 'Deputy Payroll Annual',
    nameShort: 'Payroll Annual',
    eventDetailsName: 'DEPUTY_PAYROLL_ANNUAL',
    parentName: 'Deputy Payroll',
    parentNameShort: 'Payroll',
    parentAddOn: 'payroll',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_UPFRONT,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.PAYROLL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: '',
  },
  [annualAddOns.PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID]: {
    name: 'Deputy Payroll Annual Paid Monthly',
    nameShort: 'Payroll Annual',
    eventDetailsName: 'DEPUTY_PAYROLL_ANNUAL_PAID_MONTHLY',
    parentName: 'Deputy Payroll',
    parentNameShort: 'Payroll',
    parentAddOn: 'payroll',
    paymentFrequency: PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY,
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.MONTHLY]: addOns.PAYROLL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]: annualAddOns.PAYROLL_ANNUAL_ADD_ON_ID,
    },
    featureFlagAnnualAddOn: '',
  },
};

// List of add-ons available for purchase. These don't include their annual equivalents.
export const addOnDataMap: AddOnDataMap = {
  [addOns.HR_ADD_ON_ID]: {
    name: 'Deputy HR',
    nameShort: 'HR',
    imageSource: DeputyHrImage,
    eventDetailsName: 'DEPUTY_HR',
    parentAddOn: 'hr',
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]: annualAddOns.HR_ANNUAL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    features: {
      [countryCodes.AU]: getFeatureInAllPlans(hrFeaturesWithoutLeave),
      [countryCodes.GB]: getFeatureInAllPlans(
        commonFeatures[addOns.HR_ADD_ON_ID]
      ),
      [countryCodes.US]: {
        [SUBSCRIPTION.FREE]: usHrFeatures,
        [SUBSCRIPTION.PAID1]: usHrFeatures,
        [SUBSCRIPTION.PRO_TRIAL]: usHrFeatures,
        [SUBSCRIPTION.PREMIUM]: usHrFeatures,
        [SUBSCRIPTION.FLEXI]: usHrFeatures,
        [SUBSCRIPTION.MAXI]: usHrFeatures,
        [SUBSCRIPTION.SCHEDULING_ONLY]: commonFeatures[addOns.HR_ADD_ON_ID],
        [SUBSCRIPTION.TIMESHEET_ONLY]: usHrFeatures,
        [SUBSCRIPTION.ENTERPRISE]: usHrFeatures,
        [SUBSCRIPTION.PREMIUM_YEARLY]: usHrFeatures,
        [SUBSCRIPTION.MOBILE_ONLY]: usHrFeatures,
        [SUBSCRIPTION.FREEMIUM]: usHrFeatures,
        [SUBSCRIPTION.GOOD]: usHrFeatures,
        [SUBSCRIPTION.GOOD_ANNUAL]: usHrFeatures,
        [SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY]: usHrFeatures,
        [SUBSCRIPTION.BETTER]: usHrFeatures,
        [SUBSCRIPTION.BETTER_ANNUAL]: usHrFeatures,
        [SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY]: usHrFeatures,
        [SUBSCRIPTION.BEST]: usHrFeatures,
        [SUBSCRIPTION.BEST_ANNUAL]: usHrFeatures,
        [SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY]: usHrFeatures,
      },
    },
    availableGlobally: false,
    trialLength: {
      unit: 'days',
      unitSingular: 'day',
      count: 30,
    },
    confirmSubscriptionImageSource: DeputyHRAddOnDetailsImage,
    translations: {
      title: 'shared:add-ons:hr-suite-standard:title',
      description: 'shared:add-ons:hr-suite-standard:description',
      features: {
        [FeatureKeys.DEPUTY_HIRE]:
          'shared:add-ons:hr-suite-standard:features:deputy-hire',
        [FeatureKeys.ONBOARD_HIRE]:
          'shared:add-ons:hr-suite-standard:features:onboard-hire',
        [FeatureKeys.DOCUMENT_MANAGEMENT]:
          'shared:add-ons:hr-suite-standard:features:document-management',
        [FeatureKeys.TEAM_ENGAGEMENT]:
          'shared:add-ons:hr-suite-standard:features:team-engagement',
        [FeatureKeys.LEAVE_MANAGEMENT]:
          'shared:add-ons:hr-suite-standard:features:leave-management',
        [FeatureKeys.KUDOS_REPORTING]:
          'shared:add-ons:hr-suite-standard:features:kudos-reporting',
      },
    },
    featureFlag: '',
    videoUrl: {
      [countryCodes.AU]:
        'https://player.vimeo.com/video/865389893?autoplay=1&loop=1&autopause=0&muted=1&h=964e65e7e3&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1&amp;muted=1',
      [countryCodes.GB]:
        'https://player.vimeo.com/video/865389893?autoplay=1&loop=1&autopause=0&muted=1&h=964e65e7e3&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1&amp;muted=1',
      [countryCodes.US]:
        'https://player.vimeo.com/video/989839284?autoplay=1&loop=1&autopause=0&muted=1&h=2765d16cd7&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1&amp;muted=1',
    },
    fallBackModalImageSource: '',
  },
  [addOns.ADVANCED_REPORTING_ADD_ON_ID]: {
    name: 'Analytics+',
    nameShort: 'Analytics+',
    imageSource: AdvancedReportingImage,
    eventDetailsName: 'ADVANCED_REPORTING',
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]:
        annualAddOns.ADVANCED_REPORTING_ANNUAL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    features: {
      [countryCodes.AU]: getFeatureInAllPlans(
        commonFeatures[addOns.ADVANCED_REPORTING_ADD_ON_ID]
      ),
      [countryCodes.GB]: getFeatureInAllPlans(
        commonFeatures[addOns.ADVANCED_REPORTING_ADD_ON_ID]
      ),
      [countryCodes.US]: getFeatureInAllPlans(
        commonFeatures[addOns.ADVANCED_REPORTING_ADD_ON_ID]
      ),
      [countryCodes.WW]: getFeatureInAllPlans(
        commonFeatures[addOns.ADVANCED_REPORTING_ADD_ON_ID]
      ),
    },
    availableGlobally: true,
    trialLength: {
      unit: 'days',
      unitSingular: 'day',
      count: 30,
    },
    confirmSubscriptionImageSource: AdvancedReportingAddOnDetailsImage,
    translations: {
      title: 'shared:add-ons:reporting-standard:title',
      description: 'shared:add-ons:reporting-standard:description',
      features: {
        [FeatureKeys.CUSTOMIZE_REPORTS]:
          'shared:add-ons:reporting-standard:features:customize-reports',
        [FeatureKeys.DRAG_DROP]:
          'shared:add-ons:reporting-standard:features:drag-drop',
      },
    },
    featureFlag: 'wo-1532-enable-advanced-reporting-add-on',
    videoUrl: {},
    fallBackModalImageSource: AdvancedReportingModalImage,
  },
  [addOns.PAYROLL_ADD_ON_ID]: {
    name: 'Deputy Payroll',
    nameShort: 'Payroll',
    imageSource: '',
    eventDetailsName: 'DEPUTY_PAYROLL',
    equivalentPaymentFrequencyPlans: {
      annual: annualAddOns.PAYROLL_ANNUAL_ADD_ON_ID,
      annualPaidMonthly: annualAddOns.PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    features: {},
    availableGlobally: false,
    trialLength: {
      unit: 'days',
      unitSingular: 'day',
      count: 30,
    },
    confirmSubscriptionImageSource: '',
    translations: {
      title: 'shared:add-ons:payroll-standard:title',
      description: 'shared:add-ons:payroll-standard:description',
      features: {},
    },
    featureFlag: '',
    videoUrl: {},
    fallBackModalImageSource: '',
  },
  [addOns.MESSAGING_ADD_ON_ID]: {
    name: 'Messaging+',
    nameShort: 'Messaging+',
    imageSource: '',
    eventDetailsName: 'MESSAGING_PLUS',
    equivalentPaymentFrequencyPlans: {
      [PAYMENT_FREQUENCY.ANNUAL_UPFRONT]:
        annualAddOns.MESSAGING_ANNUAL_ADD_ON_ID,
      [PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY]:
        annualAddOns.MESSAGING_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
    },
    features: {
      [countryCodes.AU]: getFeatureInAllPlans(
        commonFeatures[addOns.MESSAGING_ADD_ON_ID]
      ),
      [countryCodes.GB]: getFeatureInAllPlans(
        commonFeatures[addOns.MESSAGING_ADD_ON_ID]
      ),
      [countryCodes.US]: getFeatureInAllPlans(
        commonFeatures[addOns.MESSAGING_ADD_ON_ID]
      ),
      [countryCodes.WW]: getFeatureInAllPlans(
        commonFeatures[addOns.MESSAGING_ADD_ON_ID]
      ),
    },
    availableGlobally: true,
    trialLength: {
      unit: 'days',
      unitSingular: 'day',
      count: 30,
    },
    confirmSubscriptionImageSource: '',
    translations: {
      title: 'shared:add-ons:messaging-standard:title',
      description: 'shared:add-ons:messaging-standard:description',
      features: {
        [FeatureKeys.SHIFT_INDICATORS]:
          'shared:add-ons:messaging-standard:features:shift-indicators',
        [FeatureKeys.PERMISSIONS]:
          'shared:add-ons:messaging-standard:features:permissions',
        [FeatureKeys.MESSAGE_HISTORY]:
          'shared:add-ons:messaging-standard:features:message-history',
        [FeatureKeys.ADVANCED_MESSAGING]:
          'shared:add-ons:messaging-standard:features:advanced-messaging',
        [FeatureKeys.GROUP_CHAT_MEMBERS]:
          'shared:add-ons:messaging-standard:features:group-chat-members',
      },
    },
    featureFlag: 'wo-2210-enable-messaging-add-on',
    videoUrl: {},
    fallBackModalImageSource: MessagingAddOnModalImage,
  },
};

type LearnMoreData = {
  tenant: Partial<
    Record<CountryCode, { helpArticle: string; websitePage: string }>
  > & {
    choosePlan: string;
    thirdPartySubscription: string;
  };
  once: Partial<Record<CountryCode, { helpArticle: string }>> & {
    default: string;
  };
};

export const ADD_ON_LEARN_MORE_URLS: Record<AddOnId, LearnMoreData> = {
  [addOns.HR_ADD_ON_ID]: {
    tenant: {
      [countryCodes.AU]: {
        helpArticle: 'https://help.deputy.com/hc/en-au/articles/7351528979727',
        websitePage: 'https://www.deputy.com/au/hr-software/customer',
      },
      [countryCodes.GB]: {
        helpArticle: 'https://help.deputy.com/hc/en-au/articles/7351528979727',
        websitePage: 'https://www.deputy.com/gb/hr-software/customer',
      },
      [countryCodes.US]: {
        helpArticle: 'https://help.deputy.com/hc/en-au/articles/7351528979727',
        websitePage: 'https://www.deputy.com/hr-software/customer',
      },
      choosePlan: 'https://help.deputy.com/hc/en-au/articles/7351474686863',
      thirdPartySubscription:
        'https://help.deputy.com/hc/en-au/articles/8916201061263-How-do-I-sign-up-for-Deputy-HR-if-I-have-previously-subscribed-for-Deputy-through-a-third-party-provider',
    },
    once: {
      default: 'https://help.deputy.com/hc/en-au/articles/7351474686863',
    },
  },
  [addOns.ADVANCED_REPORTING_ADD_ON_ID]: {
    tenant: {
      [countryCodes.AU]: {
        helpArticle: 'https://help.deputy.com/hc/en-au/articles/9849016093711',
        websitePage: 'https://www.deputy.com/features/analytics',
      },
      [countryCodes.GB]: {
        helpArticle: 'https://help.deputy.com/hc/en-au/articles/9849016093711',
        websitePage: 'https://www.deputy.com/features/analytics',
      },
      [countryCodes.US]: {
        helpArticle: 'https://help.deputy.com/hc/en-au/articles/9849016093711',
        websitePage: 'https://www.deputy.com/features/analytics',
      },
      choosePlan: 'https://help.deputy.com/hc/en-au/articles/9849016093711',
      thirdPartySubscription:
        'https://help.deputy.com/hc/en-au/articles/9849016093711',
    },
    once: {
      default: 'https://help.deputy.com/hc/en-au/articles/9849016093711',
    },
  },
  [addOns.PAYROLL_ADD_ON_ID]: {
    tenant: {
      [countryCodes.AU]: {
        helpArticle:
          'https://help.deputy.com/hc/en-au/articles/12364291827983-Welcome-to-Deputy-Payroll',
        websitePage: 'https://www.deputy.com/au/payroll-software',
      },
      [countryCodes.US]: {
        helpArticle:
          'https://help.deputy.com/hc/en-au/articles/15283696533775-Getting-started-with-Deputy-Payroll-enabled-by-Paycor-US',
      },
      choosePlan:
        'https://help.deputy.com/hc/en-au/articles/12364291827983-Welcome-to-Deputy-Payroll',
      thirdPartySubscription:
        'https://help.deputy.com/hc/en-au/articles/12364291827983-Welcome-to-Deputy-Payroll',
    },
    once: {
      default:
        'https://help.deputy.com/hc/en-au/articles/12364291827983-Welcome-to-Deputy-Payroll',
      [countryCodes.US]: {
        helpArticle:
          'https://help.deputy.com/hc/en-au/articles/15283696533775-Getting-started-with-Deputy-Payroll-enabled-by-Paycor-US',
      },
    },
  },
  [addOns.MESSAGING_ADD_ON_ID]: {
    tenant: {
      [countryCodes.AU]: {
        helpArticle:
          'https://help.deputy.com/hc/en-au/articles/12628283594895-Introduction-to-Deputy-Messaging',
        websitePage: 'https://www.deputy.com/au/features/messaging',
      },
      [countryCodes.GB]: {
        helpArticle:
          'https://help.deputy.com/hc/en-au/articles/12628283594895-Introduction-to-Deputy-Messaging',
        websitePage: 'https://www.deputy.com/gb/features/messaging',
      },
      [countryCodes.US]: {
        helpArticle:
          'https://help.deputy.com/hc/en-au/articles/12628283594895-Introduction-to-Deputy-Messaging',
        websitePage: 'https://www.deputy.com/features/messaging',
      },
      choosePlan:
        'https://help.deputy.com/hc/en-au/articles/12628283594895-Introduction-to-Deputy-Messaging',
      thirdPartySubscription:
        'https://help.deputy.com/hc/en-au/articles/12628283594895-Introduction-to-Deputy-Messaging',
    },
    once: {
      default:
        'https://help.deputy.com/hc/en-au/articles/12628283594895-Introduction-to-Deputy-Messaging',
    },
  },
};

type PlanFeatures = { features: Record<string, string> };
export type RegionalUpgradeModalLeaveCard = {
  heading?: string;
  copy?: string;
};
type RegionalFeatures = {
  plans: Record<
    PlanKey,
    PlanFeatures & {
      description?: string;
      featuresHeader?: string;
    }
  >;
  // Keyed by add-on id; only the HR add-on ('hr-suite-standard') is read today.
  'upgrade-modal'?: Record<string, { leave?: RegionalUpgradeModalLeaveCard }>;
};

export const COUNTRY_TO_LOCALE: Record<string, string> = {
  AU: 'en-au',
  UK: 'en-uk',
  GB: 'en-gb',
  US: 'en-us',
};

export const REGIONAL_LOCALIZATIONS: Record<string, RegionalFeatures> = {
  'en-au': auLocalization,
  'en-gb': gbLocalization,
  'en-uk': gbLocalization,
  'en-us': usLocalization,
} as const;

// Per-country override for the HR upgrade modal's leave card. A country
// without an override renders the shared en.json strings unchanged.
export function getRegionalHrLeaveCardCopy(
  countryCode: string | undefined,
  localizations: Record<string, RegionalFeatures> = REGIONAL_LOCALIZATIONS
): RegionalUpgradeModalLeaveCard | undefined {
  const locale = countryCode && COUNTRY_TO_LOCALE[countryCode];
  if (!locale) {
    return undefined;
  }

  return localizations[locale]?.['upgrade-modal']?.[addOns.HR_ADD_ON_ID]?.leave;
}

// Plan ID for the default plan when the user is on a trial
export const PLAN_TRIAL_DEFAULT_ID = SUBSCRIPTION.BETTER;

export type PlanParent = {
  [key: string]: {
    name: string;
    key: string;
    mostPopular: boolean;
    hasAvailableAddOns: boolean;
    plans: number[];
  };
};

// WO-1897: All items related to annual paid monthly will be uncommented
// when we are ready to include it into the new plans
export const PLAN_PARENT_DETAILS: PlanParent = {
  lite: {
    name: 'Lite',
    key: 'lite',
    mostPopular: false,
    hasAvailableAddOns: true,
    plans: [
      SUBSCRIPTION.GOOD,
      SUBSCRIPTION.GOOD_ANNUAL,
      SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
    ],
  },
  core: {
    name: 'Core',
    key: 'core',
    mostPopular: true,
    hasAvailableAddOns: true,
    plans: [
      SUBSCRIPTION.BETTER,
      SUBSCRIPTION.BETTER_ANNUAL,
      SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
    ],
  },
  pro: {
    name: 'Pro',
    key: 'pro',
    mostPopular: false,
    hasAvailableAddOns: true,
    plans: [
      SUBSCRIPTION.BEST,
      SUBSCRIPTION.BEST_ANNUAL,
      SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY,
    ],
  },
};

export type PlanKey = keyof typeof PLAN_PARENT_DETAILS;

export const PLAN_PERIOD = {
  MONTHLY: {
    name: 'Monthly',
    key: 'monthly',
  },
  ANNUAL: {
    name: 'Annual',
    key: 'annual',
  },
};

export const planDetails = {
  [SUBSCRIPTION.GOOD]: {
    parent: PLAN_PARENT_DETAILS.lite,
    renewalPeriod: PLAN_PERIOD.MONTHLY,
    billingPeriod: PLAN_PERIOD.MONTHLY,
  },
  [SUBSCRIPTION.GOOD_ANNUAL]: {
    parent: PLAN_PARENT_DETAILS.lite,
    renewalPeriod: PLAN_PERIOD.ANNUAL,
    billingPeriod: PLAN_PERIOD.ANNUAL,
  },
  [SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY]: {
    parent: PLAN_PARENT_DETAILS.lite,
    renewalPeriod: PLAN_PERIOD.ANNUAL,
    billingPeriod: PLAN_PERIOD.MONTHLY,
  },
  [SUBSCRIPTION.BETTER]: {
    parent: PLAN_PARENT_DETAILS.core,
    renewalPeriod: PLAN_PERIOD.MONTHLY,
    billingPeriod: PLAN_PERIOD.MONTHLY,
  },
  [SUBSCRIPTION.BETTER_ANNUAL]: {
    parent: PLAN_PARENT_DETAILS.core,
    renewalPeriod: PLAN_PERIOD.ANNUAL,
    billingPeriod: PLAN_PERIOD.ANNUAL,
  },
  [SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY]: {
    parent: PLAN_PARENT_DETAILS.core,
    renewalPeriod: PLAN_PERIOD.ANNUAL,
    billingPeriod: PLAN_PERIOD.MONTHLY,
  },
  [SUBSCRIPTION.BEST]: {
    parent: PLAN_PARENT_DETAILS.pro,
    renewalPeriod: PLAN_PERIOD.MONTHLY,
    billingPeriod: PLAN_PERIOD.MONTHLY,
  },
  [SUBSCRIPTION.BEST_ANNUAL]: {
    parent: PLAN_PARENT_DETAILS.pro,
    renewalPeriod: PLAN_PERIOD.ANNUAL,
    billingPeriod: PLAN_PERIOD.ANNUAL,
  },
  [SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY]: {
    parent: PLAN_PARENT_DETAILS.pro,
    renewalPeriod: PLAN_PERIOD.ANNUAL,
    billingPeriod: PLAN_PERIOD.MONTHLY,
  },
};

export const HR_DOCUMENT_EVENTS_SOURCE = {
  DOCUMENT_DOWNLOAD: 'Document - Download',
};

export const POST_CONVERSION_EVENT_SOURCE = {
  POST_CONVERSION_MODAL: 'Post conversion modal',
  CHECKOUT_PAGE: 'Checkout page',
  HR_ADD_ON_MODAL: 'HR add on modal',
  ADVANCED_REPORTING_ADD_ON_MODAL: 'Advanced Reporting add on modal',
};

export const EVENTS = {
  VIEW_OPENED: 'VIEW_OPENED',
  ADD_ON_SUBSCRIBE_STARTED: 'ADD_ON_SUBSCRIBE_STARTED',
  ADD_ON_SUBSCRIBE_REQUESTED: 'ADD_ON_SUBSCRIBE_REQUESTED',
  PROMOTION_ADD_REQUESTED: 'PROMOTION_ADD_REQUESTED',
  ADD_ON_SUBSCRIBE_SUCCESS_ACKNOWLEDGED:
    'ADD_ON_SUBSCRIBE_SUCCESS_ACKNOWLEDGED',
  PROMOTION_ADD_SUCCESS_ACKNOWLEDGED: 'PROMOTION_ADD_SUCCESS_ACKNOWLEDGED',
  ADD_ON_SUBSCRIBE_FAILURE_ACKNOWLEDGED:
    'ADD_ON_SUBSCRIBE_FAILURE_ACKNOWLEDGED',
  PROMOTION_ADD_FAILURE_ACKNOWLEDGED: 'PROMOTION_ADD_FAILURE_ACKNOWLEDGED',
  PROMOTION_VALIDATE_REQUESTED: 'PROMOTION_VALIDATE_REQUESTED',
  PROMOTION_VALIDATE_SUCCESS_ACKNOWLEDGED:
    'PROMOTION_VALIDATE_SUCCESS_ACKNOWLEDGED',
  PROMOTION_VALIDATE_FAILURE_ACKNOWLEDGED:
    'PROMOTION_VALIDATE_FAILURE_ACKNOWLEDGED',
  NEWSFEED_POST_ADD_STARTED: 'NEWSFEED_POST_ADD_STARTED',
  ADD_ON_UNSUBSCRIBE_STARTED: 'ADD_ON_UNSUBSCRIBE_STARTED',
  ADD_ON_UNSUBSCRIBE_REQUESTED: 'ADD_ON_UNSUBSCRIBE_REQUESTED',
  ADD_ON_UNSUBSCRIBE_SUCCESS_ACKNOWLEDGED:
    'ADD_ON_UNSUBSCRIBE_SUCCESS_ACKNOWLEDGED',
  ADD_ON_UNSUBSCRIBE_FAILURE_ACKNOWLEDGED:
    'ADD_ON_UNSUBSCRIBE_FAILURE_ACKNOWLEDGED',
  ADD_ON_SUBSCRIPTION_UPDATE_STARTED: 'ADD_ON_SUBSCRIPTION_UPDATE_STARTED',
  ADD_ON_SUBSCRIPTION_UPDATE_REQUESTED: 'ADD_ON_SUBSCRIPTION_UPDATE_REQUESTED',
  ADD_ON_SUBSCRIPTION_UPDATE_SUCCESS_ACKNOWLEDGED:
    'ADD_ON_SUBSCRIPTION_UPDATE_SUCCESS_ACKNOWLEDGED',
  ADD_ON_SUBSCRIPTION_UPDATE_FAILURE_ACKNOWLEDGED:
    'ADD_ON_SUBSCRIPTION_UPDATE_FAILURE_ACKNOWLEDGED',
};

export const EVENT_SCREEN = {
  CHOOSE_PLAN_ONCE: 'CHOOSE_PLAN_ONCE',
  CHOOSE_PLAN_CHECKOUT: 'CHOOSE_PLAN_CHECKOUT',
};

export const EVENT_SOURCE = {
  TRY_FOR_FREE: 'Try for free',
  SUBSCRIBE: 'Subscribe',
  COMPLETE_PURCHASE: 'Complete purchase',
  SWITCH_TO_ANNUAL: 'Switch to annual',
  SWITCH_TO_MONTHLY: 'Switch to monthly',
  // Per-checkout-flow sources for PROMOTION_ADD events.
  TRIAL_CHECKOUT: 'Trial checkout',
  CHANGE_PLAN_CHECKOUT: 'Change plan checkout',
  SWITCH_BILLING_SCHEDULE_CHECKOUT: 'Change plan checkout - switch billing schedule',
  ADD_ON_TRIAL_CHECKOUT: 'Add-on trial checkout',
  ADD_ON_SUBSCRIBE_CHECKOUT: 'Add-on subscribe checkout',
  AIO_CHECKOUT: 'AIO checkout',
};

export const EVENT_TYPES = {
  POP_UP: 'POP_UP',
  URL: 'URL',
};

export const SHARED_NEWSFEED_POST_TYPE = {
  POST: 'POST',
};

export const NEWSFEED_EVENTS = {
  NEWSFEED_SHOW_CREATE_POST: 'newsfeed:show-create-post',
};

export const targetEventSourceMap = {
  [HR_EVENT_SOURCE.EMPLOYEE_PROFILE_HR_ONBOARDING_TAB]: 'onboard-existing',
  [HR_EVENT_SOURCE.EMPLOYEE_PROFILE_HR_PERSONAL_TAB]: 'onboard-existing',
  [HR_EVENT_SOURCE.PEOPLE_TAB_OUTSIDE_DROPDOWN_ONBOARD_PEOPLE]: 'onboarding',
  [HR_EVENT_SOURCE.PEOPLE_TAB_ONBOARD_PEOPLE]: 'onboarding',
  [HIRE_EVENT_SOURCE.PEOPLE_TAB]: 'hire',
  [HR_EVENT_SOURCE.EMPLOYEE_PROFILE_HR_DOCUMENTS_TAB]: 'documents',
  [HR_DOCUMENT_EVENTS_SOURCE.DOCUMENT_DOWNLOAD]: 'documents',
  'Employee modal documents action - upload': 'documents',
  'Employee modal documents action - request': 'documents',
  'Advanced reporting - main page': 'advanced-reporting',
};

export const SUBSCRIPTION_NAME_TRACKING = {
  [SUBSCRIPTION.FREE]: 'FREE',
  [SUBSCRIPTION.PAID1]: 'PAID_PRO_PREMIUM',
  [SUBSCRIPTION.PRO_TRIAL]: 'PRO_TRIAL',
  [SUBSCRIPTION.PREMIUM]: 'PAID_PRO_PREMIUM',
  [SUBSCRIPTION.FLEXI]: 'PAID_PRO_FLEXI',
  [SUBSCRIPTION.MAXI]: 'PAID_PRO_ULTIMATE',
  [SUBSCRIPTION.SCHEDULING_ONLY]: 'PAID_PRO_SCHEDULING_ONLY',
  [SUBSCRIPTION.TIMESHEET_ONLY]: 'PAID_PRO_TIMESHEET_ONLY',
  [SUBSCRIPTION.ENTERPRISE]: 'PAID_ENTERPRISE',
  [SUBSCRIPTION.PREMIUM_YEARLY]: 'YEARLY_PREMIUM',
  [SUBSCRIPTION.MOBILE_ONLY]: 'MOBILE_ONLY',
  [SUBSCRIPTION.FREEMIUM]: 'FREEMIUM',
  [SUBSCRIPTION.GOOD]: 'GOOD',
  [SUBSCRIPTION.GOOD_ANNUAL]: 'GOOD_ANNUAL',
  [SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY]: 'GOOD_ANNUAL_PAID_MONTHLY',
  [SUBSCRIPTION.BETTER]: 'BETTER',
  [SUBSCRIPTION.BETTER_ANNUAL]: 'BETTER_ANNUAL',
  [SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY]: 'BETTER_ANNUAL_PAID_MONTHLY',
  [SUBSCRIPTION.BEST]: 'BEST',
  [SUBSCRIPTION.BEST_ANNUAL]: 'BEST_ANNUAL',
  [SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY]: 'BEST_ANNUAL_PAID_MONTHLY',
};

export enum ADD_ON_SUBSCRIPTION_TYPE {
  PAID_ACTIVE = 'subscription-paid-active', // Regular active paid subscription, monthly
  PRE_IMPLEMENTATION_ACTIVE = 'subscription-pre-implementation-active', // Active subscription, implementation in progress (billing not yet started)
  TRIAL = 'subscription-trial', // Subscription in trial period
  CANCELLED_ACTIVE = 'subscription-cancelled-active', // Cancelled but active until billing period ends
  ANNUAL_INACTIVE = 'subscription-paid-annual-inactive', // Monthly plan eligible for annual conversion
  ANNUAL_ACTIVE = 'subscription-paid-annual-active', // Active annual subscription
}

export const ADD_ON_STATUS_ACTION = {
  SUBSCRIBE: 'subscribe',
  CANCEL: 'cancel',
};

// TODO: Replace with enum when all stores are using this in typescript
export const OnceRoutes = {
  Base: 'https://once.deputy.com',
  ChangePlan: '/my/#change_plan/:installationId/:planId',
};
// export enum OnceRoutes {
//   Base = 'https://once.deputy.com',
//   ChangePlan = '/my/#change_plan/:installationId/:planId',
// }

export const SUBSCRIPTION_ERROR_LOCALE_KEYS = {
  DEFAULT: 'default',
  THIRD_PARTY_SUBSCRIPTION: 'third-party-subscription',
};

// violation error code coming from the backend
export const SUBSCRIPTION_ERROR_VIOLATIONS = {
  THIRD_PARTY_SUBSCRIPTION: 'THIRD_PARTY_SUBSCRIPTION_VALIDATION',
};

export const SubscriptionErrorToLocaleKey = {
  default: SUBSCRIPTION_ERROR_LOCALE_KEYS.DEFAULT,
  [SUBSCRIPTION_ERROR_VIOLATIONS.THIRD_PARTY_SUBSCRIPTION]:
    SUBSCRIPTION_ERROR_LOCALE_KEYS.THIRD_PARTY_SUBSCRIPTION,
};

export type MutilpleAddOnData = {
  image: string;
  translations: {
    title: string;
    description: string;
    notice: string;
    primaryText: string;
    secondaryText: string;
  };
};

export const multipleAddOnData: Record<AddOnId, MutilpleAddOnData> = {
  [addOns.HR_ADD_ON_ID]: {
    image: CardHrAddOn,
    translations: {
      title: `${addOns.HR_ADD_ON_ID}:title`,
      description: `${addOns.HR_ADD_ON_ID}:description`,
      notice: `${addOns.HR_ADD_ON_ID}:notice`,
      primaryText: `${addOns.HR_ADD_ON_ID}:cta:try-now`,
      secondaryText: `${addOns.HR_ADD_ON_ID}:cta:learn-more`,
    },
  },
  [addOns.ADVANCED_REPORTING_ADD_ON_ID]: {
    image: CardAdvancedReportingAddOn,
    translations: {
      title: `${addOns.ADVANCED_REPORTING_ADD_ON_ID}:title`,
      description: `${addOns.ADVANCED_REPORTING_ADD_ON_ID}:description`,
      notice: `${addOns.ADVANCED_REPORTING_ADD_ON_ID}:notice`,
      primaryText: `${addOns.ADVANCED_REPORTING_ADD_ON_ID}:cta:try-now`,
      secondaryText: `${addOns.ADVANCED_REPORTING_ADD_ON_ID}:cta:learn-more`,
    },
  },
};

export const ADVANCED_REPORTING_ADD_ON_SOURCE = {
  UPGRADE: 'Advanced reporting upsell modal',
  TRIAL: 'Advanced reporting trial modal',
  CONFIRMATION_MODAL: 'Subscribe to Advanced reporting confirmation modal',
};

export const MESSAGING_ADD_ON_SOURCE = {
  BANNER_TRIAL: 'Messaging banner - Start 30 day trial',
  BANNER_SUBSCRIBE: 'Messaging banner - Subscribe',
  TRIAL_MODAL: 'Messaging+ trial modal',
  SUBSCRIBE_MODAL: 'Messaging+ subscribe modal',
  LEARN_MORE: 'Learn more - Messaging+',
};

export enum TabRoutes {
  Schedule = '#roster',
  People = '#team',
  Locations = '#locations',
  Reports = '#report',
  Timesheets = '#approve',
  ExportTimesheets = '#export',
  Newsfeed = '#newsfeed',
  Home = '#home',
}

export const HOME_DASHBOARD_EVENT_SCREEN = {
  [TabRoutes.People]: 'PEOPLE',
  [TabRoutes.Schedule]: 'SCHEDULE',
  [TabRoutes.ExportTimesheets]: 'TIMESHEETS_EXPORT',
  [TabRoutes.Timesheets]: 'TIMESHEETS_APPROVE',
  [TabRoutes.Locations]: 'LOCATIONS',
  [TabRoutes.Newsfeed]: 'NEWSFEED',
  [TabRoutes.Reports]: 'REPORTS',
  [TabRoutes.Home]: 'ME',
};

export const LEARN_MORE_FLEXI_URL =
  'https://help.deputy.com/hc/en-au/articles/4755613519247-Flexi-weekly-plan-FAQs';

// Organization tabs definition shared across apps
export type OrganizationTabDefinition = {
  id: string;
  labelKey: string;
  className?: string;
};

export const ORGANIZATION_TABS_DEFINITION: OrganizationTabDefinition[] = [
  { id: 'edit-org-general', labelKey: 'tabs.general', className: '' },
  {
    id: 'edit-org-billing',
    labelKey: 'tabs.billing',
    className: 'edit-org-billing-tab',
  },
  {
    id: 'edit-org-plans',
    labelKey: 'tabs.subscriptions',
    className: 'edit-org-plans-tab',
  },
  {
    id: 'edit-org-statement',
    labelKey: 'tabs.invoices',
    className: 'edit-org-statement-tab',
  },
];
